/**
 * Global launcher for the Agapaé AI assistant — a fixed FAB cloned from
 * InvitationPreviewFab (bottom-right).
 *
 * DESKTOP ONLY. On mobile the assistant is surfaced as an entry in the avatar
 * ProfileMenu instead, to avoid crowding the bottom-right corner above the
 * mobile bottom nav.
 *
 * SECURITY: renders ONLY for the couple role. Guest sessions inherit the
 * couple's plan, so a plan check alone would leak the launcher to guests —
 * the role check is mandatory (the BE blocks guests regardless). Free
 * couples DO see the launcher (they get the upsell inside the panel).
 */

import { Box, Fab, Tooltip, Zoom, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider';

// Public folder asset — the white brand symbol, served statically by Vite.
const SYMBOL_WHITE_LOGO = '/logo/symbol-logo/agapae-symbol-only-white.svg';

interface AssistantLauncherProps {
  onClick: () => void;
}

const AssistantLauncher = ({ onClick }: AssistantLauncherProps) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Desktop only — mobile uses the ProfileMenu entry instead.
  if (isMobile) return null;
  // Sandbox demos never get the companion (BE 403s regardless — AI spend).
  if (sessionUser?.role !== 'couple' || !sessionUser?.eventId || sessionUser?.isSandbox)
    return null;

  return (
    <Zoom in>
      <Tooltip title={t('assistant.product_name')} placement="left">
        <Fab
          size="large"
          onClick={onClick}
          aria-label={t('assistant.launcher_aria')}
          sx={{
            position: 'fixed',
            bottom: theme.spacing(3),
            right: theme.spacing(3),
            zIndex: theme.zIndex.speedDial,
            bgcolor: 'primary.main',
            boxShadow: theme.shadows[8],
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: theme.shadows[12],
            },
          }}
        >
          <Box
            component="img"
            src={SYMBOL_WHITE_LOGO}
            alt=""
            sx={{ height: 26, width: 'auto', display: 'block' }}
          />
        </Fab>
      </Tooltip>
    </Zoom>
  );
};

export default AssistantLauncher;
